import type { InventoryItemRow } from "~/@types/inventoryItemRow";
import type { MaterialItemDocument, SubLocationDocument } from "~/gql/graphqlBase.generated";
import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import { MaterialItemStatus } from "~/gql/graphqlBase.generated";
import hasAnyInventory from "~/helpers/hasAnyInventory";
import { INVENTORY_TAB_ROUTES } from "~fulfill/inventory/stock/helpers/inventoryContinuation";

export const HARDGOOD_STOCK_TARGET_KIND = "fulfillHardgoodStock";

export type HardgoodStockLoad = "failed" | "loading" | "matched" | "notAvailable";

interface HardgoodStockSite {
	isComplete: boolean;
	isFailed: boolean;
	siteLocationId: string;
	subLocations: readonly Pick<SubLocationDocument, "locationId" | "siteLocationId">[];
}

export interface HardgoodStockReads {
	inventory: { isComplete: boolean; isFailed: boolean; rows: readonly InventoryItemRow[] };
	material: {
		isFailed: boolean;
		isLoading: boolean;
		material: Pick<MaterialItemDocument, "id" | "status"> | undefined;
	};
	materialItemId: string;
	site: HardgoodStockSite | null;
}

export interface HardgoodStock {
	load: HardgoodStockLoad;
	rows: InventoryItemRow[];
}

const HARDGOOD_STOCK_ROUTE_PATTERN = /^\/fulfill\/inventory\/hard-goods\/([^/?#]+)$/;

export const matchHardgoodStockRoute = (pathname: string): ContinuationTarget | null => {
	const match = HARDGOOD_STOCK_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		return { targetId: decodeURIComponent(match[1]), targetKind: HARDGOOD_STOCK_TARGET_KIND };
	} catch {
		return null;
	}
};

const isAtSite = (row: InventoryItemRow, site: HardgoodStockSite): boolean =>
	row.locationId === site.siteLocationId ||
	site.subLocations.some(
		(subLocation) => subLocation.locationId === row.locationId && subLocation.siteLocationId === site.siteLocationId
	);

export const resolveHardgoodStock = ({
	inventory,
	material,
	materialItemId,
	site
}: HardgoodStockReads): HardgoodStock => {
	if (material.isFailed || inventory.isFailed || site?.isFailed) return { load: "failed", rows: [] };
	if (material.isLoading || !inventory.isComplete || (site && !site.isComplete)) return { load: "loading", rows: [] };
	const isActiveMaterial =
		material.material?.id === materialItemId && material.material.status === MaterialItemStatus.Active;
	if (!site || !isActiveMaterial) return { load: "notAvailable", rows: [] };
	const rows = inventory.rows.filter(
		(row) => row.itemId === materialItemId && isAtSite(row, site) && hasAnyInventory(row)
	);
	return rows.length > 0 ? { load: "matched", rows } : { load: "notAvailable", rows: [] };
};

const hardgoodStockReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) =>
		targetId ? `${INVENTORY_TAB_ROUTES["hard-goods"]}/${encodeURIComponent(targetId)}` : "",
	matchesRoute: matchHardgoodStockRoute
};

export default hardgoodStockReceiverIdentityAdapter;
